import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useColorTheme } from '../../context/ColorThemeContext';
import { Bot, Send, Paperclip, X, Sparkles, MessageCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AIAssistantV2 = ({ user, onCampaignGenerated, setIsGenerating, setGenerationProgress, onNavigate, sessionToken }) => {
  const { colorTheme } = useColorTheme();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [isGenerating, setIsGeneratingLocal] = useState(false);
  const [currentOptions, setCurrentOptions] = useState([]);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [aiMode, setAiMode] = useState('agent');
  const chatEndRef = useRef(null);

  // Theme helpers
  const thm = {
    gradient: colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500',
    gradientBg: colorTheme === 'purple' ? 'from-purple-50/80 via-white to-pink-50/50' : colorTheme === 'blue' ? 'from-blue-50/80 via-white to-sky-50/50' : 'from-emerald-50/80 via-white to-teal-50/50',
    ring: colorTheme === 'purple' ? 'focus:ring-purple-500' : colorTheme === 'blue' ? 'focus:ring-blue-500' : 'focus:ring-emerald-500',
    userBubble: colorTheme === 'purple' ? 'from-purple-500 to-pink-600' : colorTheme === 'blue' ? 'from-blue-500 to-sky-600' : 'from-emerald-500 to-teal-600',
    optionBorder: colorTheme === 'purple' ? 'border-purple-200 dark:border-purple-700 hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20' : colorTheme === 'blue' ? 'border-blue-200 dark:border-blue-700 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20' : 'border-emerald-200 dark:border-emerald-700 hover:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20',
    dot: colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-emerald-500',
    accent: colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-emerald-600',
    badge: colorTheme === 'purple' ? 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300' : colorTheme === 'blue' ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300' : 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300',
    btnSolid: colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700',
  };

  // Auto-scroll
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, isLoading]);

  // Initialize Agent Mode
  useEffect(() => {
    if (sessionToken && aiMode === 'agent' && messages.length === 0) {
      const init = async () => {
        try {
          const res = await axios.post(`${API}/ai-assistant/chat`, { message: "I want to create a marketing campaign", conversation_id: null }, { headers: { 'Authorization': `Bearer ${sessionToken}`, 'Content-Type': 'application/json' }, timeout: 30000 });
          setMessages([{ role: 'assistant', content: res.data.message || res.data.response, timestamp: new Date(), options: res.data.options || [] }]);
          setConversationId(res.data.conversation_id);
          setCurrentOptions(res.data.options || []);
        } catch (err) { console.error('Init error:', err); }
      };
      init();
    }
  }, [sessionToken, aiMode]);

  // Expose generation ref
  useEffect(() => {
    window.aiAssistantRef = { startGenerationAfterCreditConfirmation };
    return () => { delete window.aiAssistantRef; };
  }, []);

  // Image handlers
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please upload a valid image file'); return; }
    if (file.size > 5 * 1024 * 1024) { alert('Image size must be less than 5MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { setUploadedImage(ev.target.result); setImagePreview(URL.createObjectURL(file)); setImageFile(file); };
    reader.readAsDataURL(file);
  };
  const removeImage = () => { setUploadedImage(null); setImagePreview(null); setImageFile(null); const el = document.getElementById('ai-image-upload'); if (el) el.value = ''; };

  // Send message
  const sendMessage = async (messageText = null, isOptionSelect = false) => {
    if (typeof messageText === 'object' && messageText?.nativeEvent) messageText = null;
    const text = messageText || inputMessage.trim();
    if (!text || isLoading) return;
    setMessages(prev => [...prev, { role: 'user', content: text, timestamp: new Date(), isOptionSelect }]);
    if (!messageText) setInputMessage('');
    setCurrentOptions([]);
    setIsLoading(true);
    try {
      const payload = { message: text, conversation_id: conversationId };
      if (uploadedImage) payload.product_image = uploadedImage;
      const res = await axios.post(`${API}/ai-assistant/chat`, payload, { headers: { 'Authorization': `Bearer ${sessionToken}`, 'Content-Type': 'application/json' }, timeout: 30000 });
      const aiMsg = { role: 'assistant', content: res.data.message || res.data.response, timestamp: new Date(), options: res.data.options || [] };
      setMessages(prev => [...prev.map(m => m.role === 'assistant' ? { ...m, options: [] } : m), aiMsg]);
      setConversationId(res.data.conversation_id);
      if (res.data.is_complete && res.data.collected_data) { setCurrentOptions([]); setTimeout(() => generateCampaign(res.data.conversation_id), 2000); }
      else setCurrentOptions(res.data.options || []);
    } catch (err) {
      console.error('AI Assistant error:', err);
      const errMsg = { role: 'assistant', content: "I apologize, but I encountered an error. Let's start fresh - what product would you like to create a campaign for?", timestamp: new Date(), options: [{"text":"Electronics","value":"1"},{"text":"Beauty & Health","value":"2"},{"text":"Fashion","value":"3"},{"text":"Home & Garden","value":"4"},{"text":"Fitness","value":"5"},{"text":"Other (Type your product)","value":"6"}] };
      setMessages(prev => [...prev, errMsg]);
      setCurrentOptions(errMsg.options);
    } finally { setIsLoading(false); }
  };

  const handleAskModeMessage = async (message) => {
    if (!message.trim()) return;
    setMessages(prev => [...prev, { role: 'user', content: message, timestamp: new Date() }]);
    setInputMessage('');
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/ai-assistant/ask-mode`, { message, session_token: sessionToken }, { headers: { 'Authorization': `Bearer ${sessionToken}`, 'Content-Type': 'application/json' }, timeout: 30000 });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response || 'I apologize, please try again.', timestamp: new Date(), options: [] }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'I encountered an error. Please try again or switch to Agent Mode.', timestamp: new Date(), options: [] }]);
    } finally { setIsLoading(false); }
  };

  const handleSendMessage = () => {
    if (aiMode === 'ask') handleAskModeMessage(inputMessage);
    else sendMessage();
  };

  const handleOptionSelect = (val) => sendMessage(val, true);

  const handleButtonClick = async (action, data) => {
    if (action === 'proceed_generation') await startGenerationAfterCreditConfirmation(data.convId);
    else if (action === 'cancel_generation') setMessages(prev => [...prev, { role: 'assistant', content: 'No problem! Campaign generation cancelled. Feel free to modify your requirements or start over.', timestamp: new Date() }]);
    else if (action === 'go_to_credits') { if (onNavigate) onNavigate('credits'); }
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } };

  // Credit preview + generation
  const generateCampaign = async (convId) => {
    if (!convId) return;
    try {
      const previewRes = await axios.post(`${API}/ai-assistant/preview-credit-cost`, { conversation_id: convId }, { headers: { Authorization: `Bearer ${sessionToken}` }, timeout: 60000 });
      setCurrentOptions([]);
      const d = previewRes.data;
      setMessages(prev => [...prev, { role: 'assistant', content: `Credit Cost Preview\n\nGross Cost: ${d.gross_cost?.total || 0} credits\nFree Tier Discount: -${d.free_tier_discount?.amount || 0} credits\nNet Cost: ${d.net_cost} credits\n${d.using_free_tier ? 'Using free tier benefits!' : ''}\n\nProceed with campaign generation?`, timestamp: new Date(), hasButtons: true, buttons: [{ label: 'Proceed', action: 'proceed_generation', data: { convId } }, { label: 'Cancel', action: 'cancel_generation' }] }]);
    } catch (err) {
      setCurrentOptions([]);
      if (err.response?.status === 402) {
        const ed = err.response.data.detail || err.response.data;
        setMessages(prev => [...prev, { role: 'assistant', content: `Insufficient Credits\n\nNeed ${ed.required_credits || '?'} credits, have ${ed.current_balance || 0}.\nShortfall: ${ed.shortfall || '?'} credits.\n\nPlease add credits from Profile > Credits.`, timestamp: new Date(), hasButtons: true, buttons: [{ label: 'Add Credits', action: 'go_to_credits' }, { label: 'Cancel', action: 'cancel_generation' }] }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Unable to fetch credit preview. Please try again.', timestamp: new Date() }]);
      }
    }
  };

  const startGenerationAfterCreditConfirmation = async (convId) => {
    window.userChoseBackgroundMode = false;
    setIsGeneratingLocal(true);
    if (setIsGenerating && setGenerationProgress) {
      setIsGenerating(true);
      setGenerationProgress({ isVisible: true, currentStage: 'initializing', step: 0, total: 4, actualProgress: 0, message: 'Starting AI campaign generation...', personaCount: 2, hasPersonas: true, startTime: Date.now() });
    }
    try {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Generating your campaign... Please wait while I create amazing marketing content for you!', timestamp: new Date(), isGenerating: true }]);
      await new Promise(r => setTimeout(r, 800));
      await new Promise(r => setTimeout(r, 2000));
      const res = await axios.post(`${API}/ai-assistant/generate-campaign`, { conversation_id: convId }, { headers: { 'Authorization': `Bearer ${sessionToken}`, 'Content-Type': 'application/json' }, timeout: 300000 });
      const campaignId = res.data.id;
      setMessages(prev => [...prev, { role: 'assistant', content: 'Your campaign is being generated. Stay here to watch progress, or go to Dashboard.', timestamp: new Date() }]);

      const pollInterval = setInterval(async () => {
        try {
          const sr = await axios.get(`${API}/campaigns/${campaignId}/status`, { headers: { 'Authorization': `Bearer ${sessionToken}` } });
          const { status, progress } = sr.data;
          if (setGenerationProgress) {
            const step = progress >= 75 ? 4 : progress >= 50 ? 3 : progress >= 25 ? 2 : 1;
            setGenerationProgress(prev => ({ ...prev, step, total: 4, actualProgress: progress, message: status === 'generating' ? `Generating your ads... ${progress}% complete` : status === 'completed' ? 'Campaign generation complete!' : status === 'queued' ? 'Campaign queued...' : 'Processing...' }));
          }
          if (status === 'completed') {
            clearInterval(pollInterval);
            try {
              const full = await axios.get(`${API}/campaigns/${campaignId}`, { headers: { 'Authorization': `Bearer ${sessionToken}` } });
              onCampaignGenerated(full.data);
            } catch { onCampaignGenerated({ id: campaignId, ...res.data }); }
            setMessages(prev => [...prev, { role: 'assistant', content: 'Your campaign is ready!', timestamp: new Date() }]);
            setTimeout(() => { setIsGeneratingLocal(false); if (setIsGenerating) { setIsGenerating(false); setGenerationProgress({ step: 0, total: 0, currentStage: '', isVisible: false }); } }, 1500);
          }
        } catch {}
      }, 5000);
    } catch (err) {
      let content = 'I encountered an issue generating your campaign. Would you like to try again?';
      if (err.response?.status === 402) { const ed = err.response.data.detail || err.response.data; content = `Insufficient Credits\n\nNeed ${ed.required_credits || '?'} credits, have ${ed.current_balance || 0}. Add credits from Profile > Credits.`; }
      else if (err.response?.data) { content = `Error: ${err.response.data.detail || err.response.data.message || err.message}`; }
      setMessages(prev => [...prev, { role: 'assistant', content, timestamp: new Date() }]);
      setIsGeneratingLocal(false);
      if (setIsGenerating) { setIsGenerating(false); setGenerationProgress({ step: 0, total: 0, currentStage: '', isVisible: false }); }
    }
  };

  const switchMode = (mode) => {
    if (aiMode === mode) return;
    setAiMode(mode);
    setConversationId(null);
    setCurrentOptions([]);
    removeImage();
    if (mode === 'ask') {
      setMessages([{ role: 'assistant', content: "Hi! I'm your Q&A assistant. Ask me anything about marketing, business strategies, or any other topic!", timestamp: new Date(), options: [] }]);
    } else {
      setMessages([]);
    }
  };

  // ========== JSX ==========
  return (
    <div className={`min-h-screen bg-gradient-to-br ${thm.gradientBg} dark:from-slate-900 dark:via-slate-900 dark:to-slate-900 flex flex-col`} data-testid="ai-assistant-page">

      {/* Header */}
      <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${thm.gradient} flex items-center justify-center`}>
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="text-base font-bold text-slate-800 dark:text-white">AI Assistant</h1>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  {aiMode === 'agent' ? 'Interactive Campaign Agent' : 'Q&A Mode'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-medium px-2.5 py-1 rounded-full ${thm.badge}`}>
                {aiMode === 'agent' ? 'Agent Mode' : 'Ask Mode'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full">
        <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl shadow-sm overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 180px)' }}>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="chat-messages">
            {messages.map((msg, i) => {
              if (!msg || typeof msg !== 'object') return null;
              const role = msg.role || 'assistant';
              const content = msg.content || '';
              return (
                <div key={i}>
                  <div className={`flex ${role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl ${
                      role === 'user'
                        ? `bg-gradient-to-r ${msg.isOptionSelect ? 'from-amber-500 to-orange-500' : thm.userBubble} text-white`
                        : 'bg-slate-100/80 dark:bg-slate-700/50 text-slate-800 dark:text-white'
                    }`}>
                      <div className="flex items-start gap-2">
                        {role === 'assistant' && <Bot className={`w-4 h-4 mt-0.5 shrink-0 ${thm.accent}`} />}
                        <div className="whitespace-pre-wrap text-sm leading-relaxed">{content}</div>
                      </div>
                      {msg.timestamp && (
                        <div className={`text-[10px] mt-1.5 opacity-60 ${role === 'user' ? 'text-white/70' : 'text-slate-400'}`}>
                          {msg.timestamp.toLocaleTimeString()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Option bubbles */}
                  {role === 'assistant' && msg.options?.length > 0 && (
                    <div className="mt-2.5 ml-8 grid grid-cols-1 gap-1.5 max-w-md">
                      {msg.options.map((opt, j) => (
                        <button key={j} onClick={() => handleOptionSelect(opt.value, opt.text)} disabled={isLoading || isGenerating}
                          className={`text-left px-3.5 py-2.5 bg-white/80 dark:bg-slate-700/30 text-slate-700 dark:text-slate-200 border-2 ${thm.optionBorder} rounded-xl transition-all text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed`}
                          data-testid={`option-${j}`}
                        >{opt.text}</button>
                      ))}
                    </div>
                  )}

                  {/* Action buttons */}
                  {role === 'assistant' && msg.hasButtons && msg.buttons?.length > 0 && (
                    <div className="mt-2.5 ml-8 flex gap-2">
                      {msg.buttons.map((btn, j) => (
                        <button key={j} onClick={() => handleButtonClick(btn.action, btn.data)} disabled={isLoading || isGenerating}
                          className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-50 ${
                            btn.action === 'proceed_generation' ? `${thm.btnSolid} text-white` : 'bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-300'
                          }`}
                          data-testid={`action-btn-${btn.action}`}
                        >{btn.label}</button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Standalone options */}
            {!isLoading && !isGenerating && currentOptions.length > 0 && !messages[messages.length - 1]?.options?.length && (
              <div className="ml-8">
                <p className="text-[11px] text-slate-400 mb-1.5">Choose an option:</p>
                <div className="grid grid-cols-1 gap-1.5 max-w-md">
                  {currentOptions.map((opt, j) => (
                    <button key={j} onClick={() => handleOptionSelect(opt.value, opt.text)}
                      className={`text-left px-3.5 py-2.5 bg-white/80 dark:bg-slate-700/30 text-slate-700 dark:text-slate-200 border-2 ${thm.optionBorder} rounded-xl transition-all text-xs font-medium`}
                    >{opt.text}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Loading dots */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-slate-100/80 dark:bg-slate-700/50 px-4 py-3 rounded-2xl flex items-center gap-2">
                  <Bot className={`w-4 h-4 ${thm.accent}`} />
                  <div className="flex gap-1">
                    <div className={`w-1.5 h-1.5 ${thm.dot} rounded-full animate-pulse`} />
                    <div className={`w-1.5 h-1.5 ${thm.dot} rounded-full animate-pulse`} style={{ animationDelay: '0.15s' }} />
                    <div className={`w-1.5 h-1.5 ${thm.dot} rounded-full animate-pulse`} style={{ animationDelay: '0.3s' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Image preview */}
          {imagePreview && (
            <div className="mx-5 mb-2 p-2.5 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200/50 dark:border-slate-700/50 flex items-center gap-3">
              <img src={imagePreview} alt="Preview" className="w-12 h-12 object-cover rounded-lg border border-slate-200 dark:border-slate-600" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-700 dark:text-white">Image Attached</p>
                <p className="text-[10px] text-slate-400 truncate">{imageFile?.name}</p>
              </div>
              <button onClick={removeImage} className="p-1 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors" data-testid="remove-image-btn">
                <X className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </div>
          )}

          {/* Input area */}
          <div className="p-4 border-t border-slate-200/50 dark:border-slate-700/50">
            <div className="flex items-end gap-2.5">
              <div className="flex-1 relative">
                <textarea value={inputMessage} onChange={e => setInputMessage(e.target.value)} onKeyPress={handleKeyPress}
                  placeholder={aiMode === 'agent' ? 'Type your message...' : 'Ask me anything about marketing...'}
                  className={`w-full px-4 py-2.5 pr-10 border border-slate-200 dark:border-slate-700/50 rounded-xl focus:ring-2 ${thm.ring} focus:border-transparent dark:bg-slate-800/50 dark:text-white resize-none text-sm`}
                  rows={2} disabled={isLoading || isGenerating} data-testid="chat-input" />
                <label className="absolute right-3 bottom-2.5 cursor-pointer p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors" title="Upload image">
                  <Paperclip className="w-4 h-4" />
                  <input id="ai-image-upload" type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={isLoading || isGenerating} />
                </label>
              </div>
              <button onClick={handleSendMessage} disabled={!inputMessage.trim() || isLoading || isGenerating}
                className={`p-2.5 bg-gradient-to-r ${thm.gradient} text-white rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg`}
                data-testid="send-btn">
                {isLoading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mode toggle */}
          <div className="px-4 py-3 border-t border-slate-200/50 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-800/30">
            <div className="flex justify-center gap-2 mb-1.5">
              <button onClick={() => switchMode('agent')}
                className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-semibold transition-all ${
                  aiMode === 'agent' ? `bg-gradient-to-r ${thm.gradient} text-white shadow-lg` : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:shadow-md'
                }`} data-testid="agent-mode-btn">
                <Sparkles className="w-3.5 h-3.5" /> Campaign Agent
              </button>
              <button onClick={() => switchMode('ask')}
                className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-semibold transition-all ${
                  aiMode === 'ask' ? `bg-gradient-to-r ${thm.gradient} text-white shadow-lg` : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:shadow-md'
                }`} data-testid="ask-mode-btn">
                <MessageCircle className="w-3.5 h-3.5" /> Ask Assistant
              </button>
            </div>
            <p className="text-center text-[10px] text-slate-400">
              {aiMode === 'agent' ? 'Guided step-by-step campaign creation' : 'General Q&A for marketing advice'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantV2;
